import { readApiErrorEnvelope } from '@/lib/apiErrorEnvelope';

/**
 * Pure pieces of the driver session refresh: how a failed /auth/refresh
 * is classified, the single network-style retry, and the shared in-flight
 * call. Kept free of the store, window and import.meta so node tests can
 * exercise exactly what api.ts runs.
 */

/** What the auth event log may hold about a failure. Never a token. */
export type AuthFailureDetail = {
  /** HTTP status, or null when no response arrived (offline, timeout). */
  status: number | null;
  /** The server's error code from its envelope, when it sent one. */
  code: string | null;
  /** The server's error message ("No refresh token", "Session has been
   *  revoked..."), clipped so a verbose 5xx body cannot bloat the log. */
  message: string | null;
  /** The thrown error's name when no response arrived (TypeError,
   *  TimeoutError, AbortError, SyntaxError for a non-JSON body). */
  error: string | null;
};

export type AuthFailure = { kind: 'terminal' | 'transient' } & AuthFailureDetail;

export type RefreshOutcome = { kind: 'success'; token: string } | AuthFailure;

/** Just the loggable fields, picked explicitly so nothing else on an
 *  outcome can ever reach the event log. */
export function failureDetail(failure: AuthFailure): AuthFailureDetail {
  const { status, code, message, error } = failure;
  return { status, code, message, error };
}

const MAX_MESSAGE_LENGTH = 200;

/** Only an explicit refusal ends the session. 408, 429 and 5xx (and any
 *  other unexpected status) say nothing about the cookie, so signing the
 *  driver out on them turned a server hiccup into a lost session. */
export function isSessionEndingStatus(status: number): boolean {
  return status === 401 || status === 403;
}

/** The status of a ky HTTPError, or null for anything that never got a
 *  response. */
export function httpStatusOf(err: unknown): number | null {
  if (!(err instanceof Error) || err.name !== 'HTTPError') return null;
  const response = (err as { response?: unknown }).response;
  return response instanceof Response ? response.status : null;
}

function clip(message: string | null): string | null {
  if (message === null || message.length <= MAX_MESSAGE_LENGTH) return message;
  return message.slice(0, MAX_MESSAGE_LENGTH);
}

/** Sort a thrown refresh (or /auth/me) error into terminal or transient,
 *  with the detail the event log records. */
export async function classifyAuthFailure(err: unknown): Promise<AuthFailure> {
  const status = httpStatusOf(err);
  if (status === null) {
    const error = err instanceof Error ? err.name : typeof err;
    return { kind: 'transient', status: null, code: null, message: null, error };
  }
  // readApiErrorEnvelope clones the response, so a caller can still read it.
  const { code, message } = await readApiErrorEnvelope(err);
  return {
    kind: isSessionEndingStatus(status) ? 'terminal' : 'transient',
    status,
    code,
    message: clip(message),
    error: null,
  };
}

/** One POST /auth/refresh. `post` resolves with the parsed JSON body. */
export async function attemptRefresh(post: () => Promise<unknown>): Promise<RefreshOutcome> {
  let body: unknown;
  try {
    body = await post();
  } catch (err) {
    return classifyAuthFailure(err);
  }
  const res = (body && typeof body === 'object' ? body : {}) as {
    success?: unknown;
    data?: { access_token?: unknown } | null;
    error?: { code?: unknown; message?: unknown } | null;
  };
  const token = res.data?.access_token;
  if (res.success === true && typeof token === 'string' && token) {
    return { kind: 'success', token };
  }
  // A 200 without a token is the server declining to issue one: terminal,
  // as before. Only the envelope's code and message are kept.
  return {
    kind: 'terminal',
    status: 200,
    code: typeof res.error?.code === 'string' ? res.error.code : null,
    message: clip(typeof res.error?.message === 'string' ? res.error.message : null),
    error: null,
  };
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Refresh, and on a transient failure try once more after a short pause.
 *  Two attempts total; whatever the second says is the answer. */
export async function refreshWithOneRetry(
  post: () => Promise<unknown>,
  options: { retryDelayMs?: number; wait?: (ms: number) => Promise<void> } = {},
): Promise<RefreshOutcome> {
  const outcome = await attemptRefresh(post);
  if (outcome.kind !== 'transient') return outcome;
  await (options.wait ?? sleep)(options.retryDelayMs ?? 500);
  return attemptRefresh(post);
}

/** Run `fn`; if it throws and `shouldRetry` agrees, wait and run it once
 *  more. The second failure propagates. */
export async function retryOnce<T>(
  fn: () => Promise<T>,
  options: {
    shouldRetry: (err: unknown) => boolean;
    retryDelayMs?: number;
    wait?: (ms: number) => Promise<void>;
  },
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (!options.shouldRetry(err)) throw err;
    await (options.wait ?? sleep)(options.retryDelayMs ?? 500);
    return fn();
  }
}

/** Share one in-flight run between every caller that arrives while it is
 *  pending; the next call after it settles starts a fresh run. */
export function singleFlight<T>(run: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;
  return () => {
    if (!inFlight) {
      inFlight = run().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };
}
